import React, { useState } from 'react';
import { customLimits } from "../../assets/data/customLimits";

const PRESETS = {
  cpu: [2, 4, 8, 16, 32],
  ram: [2, 4, 8, 16, 32, 64],
  nvme: [30, 50, 100, 200, 500],
  hdd: [0, 100, 500, 1000, 2000],
};

/** Compact slider + stepper + preset chips. Fits in ~110px height. */
const SpecControl = ({ value, setValue, min, max, step, unit, presets }) => (
  <div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => setValue(Math.max(min, (Number(value) || 0) - step))}
        className="w-8 h-8 shrink-0 bg-[#033A6A] hover:bg-[#044A80] text-white rounded-lg text-lg font-bold leading-none"
      >
        −
      </button>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.min(max, Math.max(min, Number(value) || 0))}
        onChange={(e) => setValue(Number(e.target.value))}
        className="flex-1 accent-green-500 h-1"
      />
      <button
        type="button"
        onClick={() => setValue(Math.min(max, (Number(value) || 0) + step))}
        className="w-8 h-8 shrink-0 bg-[#033A6A] hover:bg-[#044A80] text-white rounded-lg text-lg font-bold leading-none"
      >
        +
      </button>
      <div className="flex items-center gap-1 bg-[#022A52] border border-[#033A6A] rounded-lg px-2 py-1 w-24 shrink-0">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            setValue(Number.isNaN(n) ? min : Math.min(max, Math.max(min, n)));
          }}
          className="w-full bg-transparent text-white text-sm font-semibold text-right outline-none"
        />
        <span className="text-[11px] text-gray-400">{unit}</span>
      </div>
    </div>
    {presets && (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setValue(p)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
              Number(value) === p
                ? 'bg-green-600 text-white'
                : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A] border border-[#033A6A]'
            }`}
          >
            {p} {unit}
          </button>
        ))}
      </div>
    )}
  </div>
);

const TABS = [
  { id: 'compute', label: '⚙️ Compute' },
  { id: 'storage', label: '💾 Storage' },
  { id: 'system', label: '🖥️ System' },
];

const CustomSpecStep = ({
  cpuCores, setCpuCores,
  ramGb, setRamGb,
  customNvme, setCustomNvme,
  customHdd, setCustomHdd,
  selectedOS, setSelectedOS,
  variants, selectedVariant, setSelectedVariant, variantLabel,
  billingCycle, setBillingCycle,
  quantity, setQuantity,
}) => {
  const [tab, setTab] = useState('compute');

  return (
    <div className="animation-fade-in text-sm">
      {/* Tab bar — splits specs so nothing scrolls */}
      <div className="grid grid-cols-3 gap-1 bg-[#022A52] rounded-xl p-1 mb-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`py-1.5 rounded-lg text-xs font-semibold transition ${
              tab === t.id ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="min-h-[228px]">
        {tab === 'compute' && (
          <div className="space-y-4">
            <div>
              <label className="block mb-1.5 font-medium text-gray-300 text-xs">vCPU Cores *</label>
              <SpecControl value={cpuCores} setValue={setCpuCores}
                min={customLimits.cpu.min} max={customLimits.cpu.max} step={customLimits.cpu.step}
                unit="core" presets={PRESETS.cpu} />
            </div>
            <div>
              <label className="block mb-1.5 font-medium text-gray-300 text-xs">RAM *</label>
              <SpecControl value={ramGb} setValue={(v) => setRamGb(Math.min(customLimits.ramGb.max, Math.max(customLimits.ramGb.min, Math.round((Number(v) || 0) / 2) * 2)))}
                min={customLimits.ramGb.min} max={customLimits.ramGb.max} step={customLimits.ramGb.step}
                unit="GB" presets={PRESETS.ram} />
            </div>
            <p className="text-[11px] text-gray-500">Tip: start small — you can extend later.</p>
          </div>
        )}

        {tab === 'storage' && (
          <div className="space-y-4">
            <div>
              <label className="block mb-1.5 font-medium text-gray-300 text-xs">NVMe Storage (fast)</label>
              <SpecControl value={customNvme} setValue={setCustomNvme}
                min={customLimits.nvmeGb.min} max={customLimits.nvmeGb.max} step={10}
                unit="GB" presets={PRESETS.nvme} />
            </div>
            <div>
              <label className="block mb-1.5 font-medium text-gray-300 text-xs">SSD Storage</label>
              <SpecControl value={customHdd} setValue={setCustomHdd}
                min={customLimits.hddGb.min} max={customLimits.hddGb.max} step={50}
                unit="GB" presets={PRESETS.hdd} />
            </div>
            <p className="text-[11px] text-gray-500">Billed per 50GB block. 0 = none.</p>
          </div>
        )}

        {tab === 'system' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block mb-1 font-medium text-gray-300 text-xs">OS *</label>
                <div className="grid grid-cols-2 gap-1 bg-[#022A52] rounded-lg p-1">
                  {['Linux', 'Windows'].map((os) => (
                    <button
                      key={os}
                      type="button"
                      onClick={() => setSelectedOS(os)}
                      className={`py-1.5 rounded-md text-xs font-semibold transition ${
                        selectedOS === os ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {os}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-300 text-xs">{variantLabel || 'Version'} *</label>
                <select
                  value={selectedVariant}
                  onChange={(e) => setSelectedVariant(e.target.value)}
                  className="w-full bg-[#022A52] border border-[#033A6A] rounded-lg p-2 text-white text-xs outline-none"
                >
                  {variants.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-1 font-medium text-gray-300 text-xs">Billing *</label>
              <div className="grid grid-cols-3 gap-1 bg-[#022A52] rounded-lg p-1">
                {['Monthly', 'Quarterly', 'Annually'].map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBillingCycle(b)}
                    className={`py-1.5 rounded-md text-xs font-semibold transition ${
                      billingCycle === b ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {b === 'Monthly' ? 'Monthly' : b === 'Quarterly' ? 'Quarterly ×3' : 'Annually ×12'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block mb-1 font-medium text-gray-300 text-xs">Quantity *</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-8 h-8 bg-[#033A6A] hover:bg-[#044A80] text-white rounded-lg text-lg font-bold leading-none"
                >
                  −
                </button>
                <input
                  type="number"
                  value={quantity}
                  min="1"
                  max="100"
                  onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  className="flex-1 bg-[#022A52] border border-[#033A6A] rounded-lg p-2 text-white text-sm text-center outline-none"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(100, quantity + 1))}
                  className="w-8 h-8 bg-[#033A6A] hover:bg-[#044A80] text-white rounded-lg text-lg font-bold leading-none"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSpecStep;
